import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, ArrowUpRight, Briefcase, CalendarDays, Inbox, Users } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtTime, initials, timeAgo } from "@/lib/format";
import { StatusBadge } from "@/components/common";

const STATUS_COLORS = { New: "#3B82F6", Active: "#10B981", Pending: "#F59E0B", Won: "#0A1128", Lost: "#EF4444", Closed: "#6B7280" };

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-border p-6 shadow-card hover:shadow-hovercard transition-shadow duration-300" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="font-serif text-4xl font-semibold text-navy mt-3">{value}</p>
          <p className="text-xs text-muted-foreground mt-2">{sub}</p>
        </div>
        <span className="h-11 w-11 bg-gold-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-gold-700" />
        </span>
      </div>
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setError("Could not load dashboard data."));
  }, []);

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-6" data-testid="overview-error">
        <p className="font-semibold text-red-700">Something went wrong</p>
        <p className="text-sm text-red-600 mt-1">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="space-y-6" data-testid="overview-skeleton">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="h-36 bg-white border border-border animate-pulse" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="h-80 bg-white border border-border animate-pulse" />
          <div className="h-80 bg-white border border-border animate-pulse" />
        </div>
      </div>
    );
  }

  const { totals, cases_by_status, clients_by_month, recent_clients, upcoming_appointments, recent_activity } = stats;

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard icon={Users} label="Total Clients" value={totals.clients} sub="Across all practice areas" testId="stat-clients" />
        <StatCard icon={Briefcase} label="Open Cases" value={totals.active_cases} sub="New, active or pending" testId="stat-cases" />
        <StatCard icon={CalendarDays} label="Appointments" value={totals.appointments_this_week} sub="Next 7 days" testId="stat-appointments" />
        <StatCard icon={Inbox} label="New Inquiries" value={totals.new_inquiries} sub="Awaiting response" testId="stat-inquiries" />
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 bg-white border border-border p-6 shadow-card" data-testid="chart-clients">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl font-semibold text-navy">New Clients</h2>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Last 6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={clients_by_month} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF5" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 13 }} />
              <Area type="monotone" dataKey="clients" stroke="#D4AF37" strokeWidth={2.5} fill="url(#goldFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 bg-white border border-border p-6 shadow-card" data-testid="chart-cases">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl font-semibold text-navy">Cases by Status</h2>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cases_by_status} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF5" vertical={false} />
              <XAxis dataKey="status" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 13 }} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
              <Bar dataKey="count" radius={0}>
                {cases_by_status.map((s) => (
                  <Cell key={s.status} fill={STATUS_COLORS[s.status] || "#0A1128"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white border border-border shadow-card" data-testid="upcoming-appointments">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <h2 className="font-serif text-xl font-semibold text-navy">Upcoming Appointments</h2>
            <Link to="/dashboard/appointments" className="text-xs font-semibold uppercase tracking-wider text-gold-700 hover:text-navy inline-flex items-center gap-1" data-testid="view-all-appointments">
              Calendar <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {upcoming_appointments.length === 0 && (
              <p className="px-6 py-8 text-sm text-muted-foreground">No appointments scheduled.</p>
            )}
            {upcoming_appointments.map((a) => (
              <button key={a.id} onClick={() => navigate("/dashboard/appointments")}
                className="w-full text-left px-6 py-4 hover:bg-muted/60 transition-colors" data-testid={`upcoming-appt-${a.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-navy truncate">{a.client_name}</p>
                  <span className="text-xs text-gold-700 font-semibold whitespace-nowrap">{fmtDate(a.date)} · {fmtTime(a.time)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{a.type} — {a.location}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border shadow-card" data-testid="recent-clients">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <h2 className="font-serif text-xl font-semibold text-navy">Recent Clients</h2>
            <Link to="/dashboard/clients" className="text-xs font-semibold uppercase tracking-wider text-gold-700 hover:text-navy inline-flex items-center gap-1" data-testid="view-all-clients">
              All clients <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {recent_clients.map((c) => (
              <button key={c.id} onClick={() => navigate(`/dashboard/clients/${c.id}`)}
                className="w-full text-left px-6 py-4 hover:bg-muted/60 transition-colors flex items-center gap-3" data-testid={`recent-client-${c.id}`}>
                <span className="h-9 w-9 bg-navy text-gold text-xs font-serif font-semibold flex items-center justify-center shrink-0">
                  {initials(c.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-navy truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.case_type}</p>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border shadow-card" data-testid="recent-activity">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <h2 className="font-serif text-xl font-semibold text-navy">Recent Activity</h2>
          </div>
          <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
            {recent_activity.map((a) => (
              <div key={a.id} className="px-6 py-4 flex gap-3" data-testid={`activity-${a.id}`}>
                <span className="h-2 w-2 bg-gold mt-1.5 shrink-0" />
                <div>
                  <p className="text-sm text-navy leading-snug">{a.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">{timeAgo(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-navy text-ivory p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hero-grain relative" data-testid="overview-quick-actions">
        <div className="relative z-10">
          <h2 className="font-serif text-2xl font-semibold">Quick actions</h2>
          <p className="text-sm text-ivory/60 mt-1">The three things you do most, one click away.</p>
        </div>
        <div className="flex flex-wrap gap-3 relative z-10">
          <Link to="/dashboard/clients?new=1" data-testid="quick-add-client"
            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors font-semibold uppercase tracking-wider text-xs px-5 py-3">
            Add Client <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link to="/dashboard/appointments?new=1" data-testid="quick-schedule"
            className="inline-flex items-center gap-2 border border-ivory/40 text-ivory hover:border-gold hover:text-gold transition-colors font-semibold uppercase tracking-wider text-xs px-5 py-3">
            Schedule Appointment
          </Link>
          <Link to="/dashboard/blog/new" data-testid="quick-new-post"
            className="inline-flex items-center gap-2 border border-ivory/40 text-ivory hover:border-gold hover:text-gold transition-colors font-semibold uppercase tracking-wider text-xs px-5 py-3">
            Draft Blog Post
          </Link>
        </div>
      </div>
    </div>
  );
}
